import { pick } from '@npi/buddy-utils';

import { Principal } from 'src/types/AddMerchant/merchantInfo';

import {
    Documents,
    FormattedDocumentOption,
    MerchantInformation,
    PrincipalDocument,
} from '../types';

/**
 * Utility functions for handling merchant application page
 */

const SUPPORTED_DOCUMENTS = [
    'b1FormFile',
    'businessReportFile',
    'partnershipDeedFile',
    'otherFile',
    'proofOfBank',
    'principalDocuments',
];

/**
 * Picks relevant supporting documents from the provided object.
 * This function is used to extract specific document fields from the merchant application data.
 * It returns an object containing only the specified fields.
 *
 * @param supportingDocuments The object containing all supporting documents.
 * * @returns An object with only the specified supporting document fields.
 */
export function pickSupportingDocuments(supportingDocuments: Record<string, unknown>) {
    return pick(supportingDocuments, SUPPORTED_DOCUMENTS);
}

/**
 * Formats documents into a standardized array of value-label pairs
 * for UI dropdowns or select inputs.
 *
 * @param documents - The documents object containing file data
 * @returns An array of formatted document options
 */
export const formatDocumentOptions = (documents: Documents): FormattedDocumentOption[] => {
    const newData: FormattedDocumentOption[] = [];

    Object.entries(documents).forEach(([docType, file]) => {
        if (docType === 'proofOfBank') {
            const proofOfBankFiles = Object.values(file || {});

            proofOfBankFiles.forEach((proofOfBankFile, index) => {
                if (proofOfBankFile?.locationUrl) {
                    const fileName = `Proof of Bank Statement (${docType} - Statement ${
                        index + 1
                    })`;
                    newData.push({
                        value: proofOfBankFile.locationUrl,
                        label: fileName,
                    });
                }
            });
        } else if (Array.isArray(file)) {
            file.forEach((item, index) => {
                if (item?.locationUrl) {
                    const fileName = `Supporting Document (${docType} ${index + 1})`;
                    newData.push({
                        value: item.locationUrl,
                        label: fileName,
                    });
                }
            });
        } else if (file?.locationUrl) {
            const fileName = `Supporting Document (${docType})`;
            newData.push({
                value: file.locationUrl,
                label: fileName,
            });
        }
    });

    return newData;
};

/**
 * Creates properly labeled document entries for principal documents
 * Maps principal documents to their corresponding principal information to ensure proper naming
 *
 * @param principalDocuments - Array of principal documents
 * @param merchantInformation - Merchant information containing principals
 * @returns Array of objects with value and label for each document
 */
export const createLabeledPrincipalDocuments = (
    principalDocuments: PrincipalDocument[] = [],
    merchantInformation: MerchantInformation = {}
): Array<{
    value: string;
    label: string;
}> => {
    const newData: Array<{ value: string; label: string }> = [];
    const allPrincipals = merchantInformation.principals || [];

    principalDocuments.forEach((doc, docIndex) => {
        const correspondingPrincipal = findCorrespondingPrincipal(
            allPrincipals,
            docIndex,
            principalDocuments
        );

        const firstName = correspondingPrincipal?.firstName ?? '';
        const lastName = correspondingPrincipal?.lastName ?? '';
        const fullName = `${firstName} ${lastName}`;

        if (doc.idFile?.locationUrl) {
            newData.push({
                value: doc.idFile.locationUrl,
                label: `Proof of ID (${fullName})`,
            });
        }

        if (doc.addressFile?.locationUrl) {
            newData.push({
                value: doc.addressFile.locationUrl,
                label: `Proof of Address (${fullName})`,
            });
        }
    });

    return newData;
};

/**
 * Finds the corresponding principal for a given principal document
 * Handles the case where principalDocuments may be filtered (e.g., only beneficial owners)
 *
 * @param allPrincipals - Array of all principals
 * @param docIndex - Index of the principal document to find
 * @param principalDocuments - Array of principal documents
 * @returns The corresponding principal or null if not found
 */
export const findCorrespondingPrincipal = (
    allPrincipals: Principal[],
    docIndex: number,
    principalDocuments: PrincipalDocument[]
): Principal => {
    // If documents are filtered by beneficial owners, we need to find the correct mapping
    const beneficialOwnerIndices = allPrincipals
        .map((principal, index) => {
            return principal?.isBeneficialOwner ? index : -1;
        })
        .filter((index) => {
            return index !== -1;
        });

    // If we have beneficial owner filtering, use that mapping
    if (beneficialOwnerIndices.length === principalDocuments.length) {
        const actualPrincipalIndex = beneficialOwnerIndices[docIndex];
        return allPrincipals[actualPrincipalIndex];
    }

    // Fallback to direct index mapping
    return allPrincipals[docIndex];
};
